'use client'

import { useCallback } from 'react'

import { useCachedSWR } from '@/core/lib/swr/use-cached-swr'
import { createSessionSwrScope } from '@/core/lib/swr/swr.key'

import { viewerSchema, type Viewer } from '../schemas/viewer.schema'

import type { CachePolicyInput } from '@/core/lib/cache/cache.policy'
import type { SwrKey } from '@/core/lib/swr/swr.key'

const VIEWER_KEY: SwrKey = [
  '/viewer',
  { scope: createSessionSwrScope('viewer') },
]
const VIEWER_POLICY: CachePolicyInput = {
  preset: 'REALTIME',
  swr: { dedupingInterval: 30_000 },
}
const VIEWER_OPTIONS = { responseSchema: viewerSchema }

export function useViewer() {
  const { data, isLoading, mutate } = useCachedSWR<Viewer>(
    VIEWER_KEY,
    VIEWER_POLICY,
    VIEWER_OPTIONS
  )

  const refresh = useCallback(() => mutate(), [mutate])
  const patch = useCallback(
    (changes: Partial<Viewer>) =>
      mutate(
        (current) =>
          current?.ok
            ? { ...current, data: { ...current.data, ...changes } }
            : current,
        { revalidate: (next) => next === undefined }
      ),
    [mutate]
  )

  return {
    viewer: data?.ok ? data.data : undefined,
    isLoading,
    refresh,
    patch,
  }
}
